import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { dynaVaultsAPI } from '../../../../api/DynaVaultsAPI';
import { QueryOptions } from 'utils/useQueryUtils';
import { useConnection } from 'wagmi';
import { getStoredTokens } from 'structure/portfolio/utils/portfolio';
import { queryKeys } from 'structure/pv2/vault/configs/queryKeys';
import { useVaultListBlockchain } from 'structure/pv2/dynavault/hooks/useVaultListBlockchain';
import { ChainId } from 'sfi-vault-sdk';
import {
  enrichVaultsWithDetails,
  getVaultIdentityKey,
  mergeVaultLists,
} from 'structure/pv2/dynavault/utils/mergeVaultLists';

export const useVaultList = (options?: QueryOptions) => {
  const { query } = options || {};

  const account = useConnection();
  const tokens = getStoredTokens();
  const authToken = tokens[account.address || ''];

  const useQueryOptions = {
    queryKey: queryKeys.vaultList({
      walletAddress: account.address,
      isAuth: !!authToken,
    }),
    ...query,
  };

  return useQuery({
    placeholderData: keepPreviousData,
    queryFn: () => {
      const tokens = getStoredTokens();
      const authToken = tokens[account.address ?? ''];
      return dynaVaultsAPI.loadVaults(authToken, account.address);
    },
    ...useQueryOptions,
  });
};

export const useVaultListWithFallback = (options?: QueryOptions) => {
  const vaultListQuery = useVaultList(options);
  const isApiError =
    vaultListQuery.isFetched &&
    (Boolean(vaultListQuery.data) === false ||
      (vaultListQuery.data?.length ?? 0) === 0);

  const vaultListBlockchainQuery = useVaultListBlockchain({
    ...options,
    chainId: ChainId.BASE,
    query: {
      ...options?.query,
      enabled: isApiError,
    },
  });

  if (isApiError) {
    return vaultListBlockchainQuery;
  }

  return vaultListQuery;
};

export const useVaultListAcrossChains = (options?: QueryOptions) => {
  const vaultListQuery = useVaultList(options);
  const ethereumVaultListQuery = useVaultListBlockchain({
    ...options,
    chainId: ChainId.ETHEREUM,
    query: {
      ...options?.query,
      enabled: options?.query?.enabled !== false,
    },
  });
  const baseVaultListQuery = useVaultListBlockchain({
    ...options,
    chainId: ChainId.BASE,
    query: {
      ...options?.query,
      enabled: options?.query?.enabled !== false,
    },
  });
  const bnbVaultListQuery = useVaultListBlockchain({
    ...options,
    chainId: ChainId.BNB,
    query: {
      ...options?.query,
      enabled: options?.query?.enabled !== false,
    },
  });

  const registryVaults = mergeVaultLists(
    ethereumVaultListQuery.data,
    baseVaultListQuery.data,
    bnbVaultListQuery.data
  );
  const apiVaultKeys = new Set(
    vaultListQuery.data?.map(getVaultIdentityKey) ?? []
  );
  const registryOnlyVaults = registryVaults.filter(
    (vault) => !apiVaultKeys.has(getVaultIdentityKey(vault))
  );
  const detailQueries = useQueries({
    queries: registryOnlyVaults.map((vault) => ({
      queryKey: queryKeys.vault({
        address: vault.address,
        chainId: vault.chainId,
      }),
      queryFn: () =>
        dynaVaultsAPI.loadVault({
          address: vault.address,
          chainId: vault.chainId,
        }),
      enabled: vaultListQuery.isFetched,
      staleTime: 60 * 1000,
      retry: 3,
    })),
  });

  const data = enrichVaultsWithDetails(
    mergeVaultLists(registryVaults, vaultListQuery.data),
    detailQueries.map((query) => query.data)
  );

  return {
    ...vaultListQuery,
    data,
    isLoading:
      vaultListQuery.isLoading ||
      ethereumVaultListQuery.isLoading ||
      baseVaultListQuery.isLoading ||
      bnbVaultListQuery.isLoading ||
      detailQueries.some((query) => query.isLoading),
    isFetched:
      vaultListQuery.isFetched &&
      ethereumVaultListQuery.isFetched &&
      baseVaultListQuery.isFetched &&
      bnbVaultListQuery.isFetched &&
      detailQueries.every((query) => query.isFetched),
  };
};
